import { IconFont } from '@/components';
import { Button, Card, Form, Modal } from '@arco-design/web-react';
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ContainerSecurityContext, useSecurityContextStore } from '../container-security-context';
import { DockerSetting, useDockerSettingStore } from '../docker-setting';
import { EnvironmentVariable, useEnvironmentVarStore } from '../environment-variable';
import { HealthCheck, useHealthCheckStore } from '../health-check';
import { useResetWorkload } from '../hooks';
import { ImageOptions, useImageOptionsStore } from '../image-options';
import { LifecycleManage, useLifecycleManageStore } from '../lifecycle-manage';
import { PortSetting, usePortSettingStore } from '../port-setting';
import { StartupCmd, useStartupCmdStore } from '../startup-cmd';
import { SyncHostTimezone, useSyncHostTimezoneStore } from '../sync-host-timezone';
import { GrayBox } from '../tools';
import { WorkloadComponent, WorkloadType } from '../types';
import { ContainerItem, useContainerStore } from './useContainerStore';

const FormItem = Form.Item;
const { DEPLOYMENT } = WorkloadType;

interface ContainerProps extends WorkloadComponent {
  /** 编辑的时候需要隐藏服务端口 */
  hiddrenServicePort?: boolean;
  /** 更多链接 */
  moreLink?: string;
}
/** 容器 */
export function Container({ module = DEPLOYMENT, hiddrenServicePort, moreLink }: ContainerProps) {
  const { t } = useTranslation();
  const msg = {
    container: t('common:container'),
    container_add: t('components:add.container'),
    description: t('components:customize.container.settings.to.create.a.container'),
    add: t('common:add'),
    image: t('components:image'),
    no_limit: t('components:unlimited'),
    invalid_containers: t('components:please.add.at.least.one.container'),
  };

  const [visible, setVisible] = useState(false);
  const [editName, setEditName] = useState<string>();
  const validateContainer = useDockerSettingStore((state) => state.validateDockerSetting);
  const validatePort = usePortSettingStore((state) => state.validate);
  const {
    containersArray,
    invalidate,
    addContainer,
    updateContainer,
    removeContainer,
    initEditContainer,
  } = useContainerStore();
  const invalidateMsg = invalidate ? msg.invalid_containers : '';

  // 点击新增
  const handleClickAdd = () => {
    setVisible(true);
  };
  // 点击编辑
  const handleClickEdit = (name: string) => {
    setEditName(name);
    setVisible(true);
    initEditContainer(name);
  };
  // 点击删除
  const handleClickDelete = (name: string) => {
    removeContainer(name);
  };

  // modal 取消按钮
  const handleCancel = () => {
    setVisible(false);
    setEditName(undefined);
    useResetWorkload.getState().resetContainerModal();
  };

  // modal 保存按钮
  const handleSave = async () => {
    const validContainer = await validateContainer();
    const validPort = await validatePort();
    const isValid = validContainer && validPort;

    if (!isValid) return;

    const container: ContainerItem = {
      containerSetting: useDockerSettingStore.getState().dockerSetting,
      portSetting: usePortSettingStore.getState().ports,
      imagePullPolicy: useImageOptionsStore.getState().imagePullPolicy,
      livenessProbe: useHealthCheckStore.getState().aliveCheck,
      readinessProbe: useHealthCheckStore.getState().readyCheck,
      startupProbe: useHealthCheckStore.getState().startupCheck,
      lifecycle: {
        postStart: useLifecycleManageStore.getState().postStartAction,
        preStop: useLifecycleManageStore.getState().preStopAction,
      },
      command: useStartupCmdStore.getState().executionCommand,
      args: useStartupCmdStore.getState().parameters,
      tty: useStartupCmdStore.getState().tty,
      env: useEnvironmentVarStore.getState().env,
      securityContext: useSecurityContextStore.getState().securityContextData,
      syncHostTimezone: useSyncHostTimezoneStore.getState().isSyncHostTimezoneSelected,
    };

    if (!editName) {
      addContainer(container);
    } else {
      updateContainer(editName, container);
    }
    handleCancel();
  };

  // 没有容器的情况
  const NoContainerMarkup = (
    <ContainerCard hoverable $pointer onClick={handleClickAdd}>
      <PlaceholderBox>
        <IconFont name="icon-rongqijiuxu" style={{ fontSize: 36 }} />
        <Title>{msg.container_add}</Title>
        <Desc>{msg.description}</Desc>
      </PlaceholderBox>
    </ContainerCard>
  );

  const ContainerMarkup = (
    <ContainerCard>
      <ContainersBox>
        {containersArray?.map((container) => {
          const cpuLimit = container.containerSetting.cpuLimit;
          const cpuReserve = container.containerSetting.cpuReserve;
          const memoryLimit = container.containerSetting.memoryLimit;
          const memoryReserve = container.containerSetting.memoryReserve;
          const noMemoryLimit = !memoryReserve && !memoryLimit;
          const noCpuLimit = !cpuReserve && !cpuLimit;

          return (
            <ItemBox key={container.containerSetting.containerName}>
              <ItemImageBox>
                <IconFont name="icon-rongqijiuxu" style={{ fontSize: 36 }} />
                <div className="TITLE_BOX">
                  <div className="TITLE">{container.containerSetting.containerName}</div>
                  <div className="DESC">
                    {msg.image}: {container.containerSetting.image}
                  </div>
                </div>
              </ItemImageBox>
              <ItemResourceBox>
                <div className="MEMORY">
                  <IconFont name="icon-memory" />
                  <span>
                    {noMemoryLimit ? msg.no_limit : `${memoryReserve} MiB - ${memoryLimit} MiB`}
                  </span>
                </div>
                <div className="CPU">
                  <IconFont name="icon-chip" />
                  <span>{noCpuLimit ? msg.no_limit : `${cpuReserve} - ${cpuLimit}`}</span>
                </div>
              </ItemResourceBox>
              <ItemActionBox>
                <Button
                  className="EDIT"
                  icon={<IconEdit />}
                  type="text"
                  onClick={() => handleClickEdit(container.containerSetting.containerName)}
                />
                <Button
                  icon={<IconDelete />}
                  type="text"
                  status="danger"
                  onClick={() => handleClickDelete(container.containerSetting.containerName)}
                />
              </ItemActionBox>
            </ItemBox>
          );
        })}
        <div>
          <Button icon={<IconPlus />} type="primary" size="mini" onClick={handleClickAdd}>
            {msg.add}
          </Button>
        </div>
      </ContainersBox>
    </ContainerCard>
  );

  return (
    <>
      <FormItem
        layout="vertical"
        label={msg.container}
        validateStatus={invalidate ? 'error' : undefined}
        help={invalidateMsg}
      >
        {!containersArray?.length ? NoContainerMarkup : ContainerMarkup}
      </FormItem>

      <StyledModal
        style={{ width: 960 }}
        title={msg.container_add}
        visible={visible}
        onOk={handleSave}
        onCancel={handleCancel}
        autoFocus={false}
        focusLock={false}
      >
        <Content>
          <DockerSetting module={module} moreLink={moreLink} />
          <PortSetting module={module} hiddrenServicePort={hiddrenServicePort} />
          <ImageOptions />
          <HealthCheck />
          <LifecycleManage />
          <StartupCmd />
          <EnvironmentVariable visible={visible} />
          <ContainerSecurityContext visible={visible} />
          <SyncHostTimezone />
        </Content>
      </StyledModal>
    </>
  );
}

/** ============================ 样式 ==================================== */
const StyledModal = styled(Modal)`
  .arco-modal-content {
    max-height: 80vh;
    overflow-y: auto;
  }
`;

const ContainerCard = styled(Card)<{ $pointer?: boolean }>`
  border-style: dashed;
  &:hover {
    ${({ $pointer }) =>
      $pointer &&
      css`
        border-style: solid;
        cursor: pointer;
      `};
  }
`;
const PlaceholderBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Title = styled.div`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;
const Desc = styled.div`
  opacity: 0.45;
`;

const Content = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

const ContainersBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemBox = styled(GrayBox)`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
`;

const ItemImageBox = styled.div`
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
  .TITLE_BOX {
    color: #000;
    .TITLE {
    }
    .DESC {
      font-size: 12px;
      opacity: 0.45;
    }
  }
`;

const ItemResourceBox = styled.div`
  display: flex;
  gap: 8px;
  .MEMORY,
  .CPU {
    display: flex;
    gap: 4px;
    align-items: center;
  }
`;

const ItemActionBox = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 1rem;
`;
